import {
    STORE_ALERT_RULE,
    UPDATE_ALERT_RULE,
    DELETE_ALERT_RULE,
    VIEW_NOTIFIABLE,
    VIEW_ALERT_RULES,
    VIEW_ALERT_RULE,
    VIEW_ALERTS_BY_RULE,
    SUBSCRIPTION_ALERT_BY_RULE,
} from '../../schema/alertRules';

export default {
    namespaced: true,
    state: {
        alert_rule: null,
        alert_rules: [],
        notifiable: [],
        alerts: [],
        alert_rules_pagination: null,
        alerts_pagination: null,
    },
    getters: {
        alert_rules: (state) => {
            return state.alert_rules;
        },
        alert_rule_by_id: (state) => (id) => {
            return state.alert_rules.find((item) => item.id === id);
        },
        alert_rule: (state) => {
            return state.alert_rule;
        },
        notifiable: (state) => {
            return state.notifiable;
        },
        get_alerts: (state) => {
            return state.alerts;
        },
        alert_rules_pagination: (state) => {
            return state.alert_rules_pagination;
        },

        alerts_pagination: (state) => {
            return state.alerts_pagination;
        },
    },
    mutations: {
        clear: (state) => {
            state.alert_rules = [];
        },
        set_notifiable: (state, items) => {
            state.notifiable = items;
        },
        update: (state, item) => {
            const index = state.alert_rules.findIndex((a) => a.id === item.id);
            if (index !== -1) {
                state.alert_rules.splice(index, 1, item);
            } else {
                state.alert_rules.push(item);
            }
        },

        delete: (state, id) => {
            const index = state.alert_rules.findIndex((a) => a.id === id);
            if (index !== -1) {
                state.alert_rules.splice(index, 1);
            }
        },
        set_alert_rules: (state, items) => {
            state.alert_rules = Array.isArray(items) ? items : [];
        },
        set_alert_rule: (state, item) => {
            state.alert_rule = item;
        },
        set_alert_rules_pagination: (state, item) => {
            state.alert_rules_pagination = item;
        },
        set_alerts: (state, alerts) => {
            state.alerts = alerts;
        },
        set_alerts_pagination: (state, item) => {
            state.alerts_pagination = item;
        },
        updateAlerts: (state, alert) => {
            const index = state.alerts.findIndex((a) => a.id === alert.id);
            if (index !== -1) {
                state.alerts.splice(index, 1, alert);
            } else {
                state.alerts.push(alert);
            }
        },
    },
    actions: {
        load: ({ commit, rootGetters }, payload) => {
            const projectId = parseInt(payload.project_id);
            const id = parseInt(payload.id);

            if (isNaN(projectId) || projectId === 0 || isNaN(id)) {
                const errorMsg = `Invalid parameters for loading alert rule: projectId=${payload.project_id}, id=${payload.id}`;
                console.error(errorMsg);
                commit('set_alert_rule', null); // Ensure state is cleared
                return Promise.reject(new Error(errorMsg));
            }

            return new Promise((resolve, reject) => {
                rootGetters.apollo
                    .query({
                        query: VIEW_ALERT_RULE,
                        variables: {
                            projectId: projectId,
                            id: id,
                        },
                        fetchPolicy: 'no-cache',
                        notifyOnNetworkStatusChange: true,
                    })
                    .then(({ data, loading }) => {
                        if (loading) {
                            return;
                        }

                        if (data && data.alertRule !== undefined) {
                            commit('set_alert_rule', data.alertRule);
                            resolve(data.alertRule);
                        } else {
                            const errorMsg =
                                'No data or unexpected response structure received from alertRule query when loading is complete.';
                            console.error(errorMsg);
                            commit('set_alert_rule', null);
                            reject(new Error(errorMsg));
                        }
                    })
                    .catch((error) => {
                        console.error(
                            'GraphQL query error in alertRules/load:',
                            error.message
                        );
                        commit('set_alert_rule', null);
                        reject(error);
                    });
            });
        },
        list: ({ commit, rootGetters }, payload) => {
            return new Promise((resolve, reject) => {
                rootGetters.apollo
                    .query({
                        query: VIEW_ALERT_RULES,
                        variables: {
                            projectId: parseInt(payload.project_id),
                            filters: payload.filters, // Pass the filters from the payload
                        },
                        fetchPolicy: 'no-cache',
                        notifyOnNetworkStatusChange: true,
                    })
                    .then(({ data, loading }) => {
                        if (!loading && data !== undefined) {
                            commit('set_alert_rules', data.alertRules.data);
                            commit(
                                'set_alert_rules_pagination',
                                data.alertRules.pagination
                            );
                            resolve(data.alertRules); // Resolve with the full paginated object
                        } else {
                            reject(
                                new Error(
                                    'No data received from alertRules query, query returned undefined data, or alertRules field is missing.'
                                )
                            );
                        }
                    })
                    .catch((error) => {
                        console.error(error.message);
                        reject(error);
                    });
            });
        },
        storeOrUpdate: ({ commit, rootGetters }, payload) => {
            let typeName = payload.create
                ? 'storeAlertRule'
                : 'updateAlertRule';

            return new Promise((resolve, reject) => {
                rootGetters.apollo
                    .mutate({
                        mutation: payload.create
                            ? STORE_ALERT_RULE
                            : UPDATE_ALERT_RULE,
                        variables: {
                            id: payload.id,
                            projectId: payload.projectId,
                            input: payload.input,
                        },
                        fetchPolicy: 'no-cache',
                        notifyOnNetworkStatusChange: true,
                    })
                    .then(({ data, loading }) => {
                        if (!loading && data !== undefined) {
                            commit('update', data[typeName]);
                            resolve(data[typeName]);
                        }
                    })
                    .catch((error) => {
                        console.error(error.message);
                        reject(error);
                    });
            });
        },
        delete: ({ commit, rootGetters }, payload) => {
            return new Promise((resolve, reject) => {
                rootGetters.apollo
                    .mutate({
                        mutation: DELETE_ALERT_RULE,
                        variables: {
                            id: payload.id,
                            projectId: payload.project_id,
                        },
                        fetchPolicy: 'no-cache',
                        notifyOnNetworkStatusChange: true,
                    })
                    .then(({ data, loading }) => {
                        if (!loading && data !== undefined) {
                            commit('delete', payload.id);
                            resolve(data.deleteAlertRule);
                        }
                    })
                    .catch((error) => {
                        console.error(error.message);
                        reject(error);
                    });
            });
        },
        load_notifiable: ({ commit, rootGetters }, payload) => {
            return new Promise((resolve, reject) => {
                rootGetters.apollo
                    .query({
                        query: VIEW_NOTIFIABLE,
                        variables: {
                            projectId: payload.project_id,
                        },
                        fetchPolicy: 'no-cache',
                        notifyOnNetworkStatusChange: true,
                    })
                    .then(({ data, loading }) => {
                        if (!loading && data !== undefined) {
                            commit('set_notifiable', data.alertNotifiable);
                            resolve(data.alertNotifiable);
                        }
                    })
                    .catch((error) => {
                        console.error(error.message);
                        reject(error);
                    });
            });
        },
        load_alerts: ({ commit, rootGetters }, payload) => {
            const projectId = parseInt(payload.project_id);
            const id = parseInt(payload.id);

            if (isNaN(projectId) || projectId === 0 || isNaN(id)) {
                const errorMsg = `Invalid parameters for loading alert rule: projectId=${payload.project_id}, id=${payload.id}`;
                console.error(errorMsg);
                commit('set_alert_rule', null); // Ensure state is cleared
                return Promise.reject(new Error(errorMsg));
            }

            return new Promise((resolve, reject) => {
                rootGetters.apollo
                    .query({
                        query: VIEW_ALERTS_BY_RULE,
                        variables: {
                            projectId: projectId,
                            ruleId: id,
                            filters: payload.filters,
                        },
                        fetchPolicy: 'no-cache',
                        notifyOnNetworkStatusChange: true,
                    })
                    .then(({ data, loading }) => {
                        if (loading) {
                            return;
                        }

                        if (data && data.alertsByRule !== undefined) {
                            commit('set_alerts', data.alertsByRule.data);
                            commit(
                                'set_alerts_pagination',
                                data.alertsByRule.pagination
                            );
                            resolve(data.alertsByRule);
                        } else {
                            const errorMsg =
                                'No data or unexpected response structure received from alertsByRule query when loading is complete.';
                            console.error(errorMsg);
                            commit('set_alerts', null);
                            reject(new Error(errorMsg));
                        }
                    })
                    .catch((error) => {
                        console.error(
                            'GraphQL query error in alertRules/load:',
                            error.message
                        );
                        commit('set_alerts', null);
                        reject(error);
                    });
            });
        },
        subscribeToAlerts: ({ commit, rootGetters }, payload) => {
            rootGetters.apollo
                .subscribe({
                    query: SUBSCRIPTION_ALERT_BY_RULE,
                    variables: {
                        projectId: payload.projectId,
                        ruleId: payload.ruleId,
                    },
                })
                .subscribe({
                    async next(message) {
                        commit('updateAlerts', message.data.liveAlertsByRule);
                    },
                    error(error) {
                        console.error('error', error);
                    },
                });
        },
    },
};
